/**
 * Network Management Routes
 *
 * Each child path maps to its own React parcel component matching the legacy app routing.
 * legacy app parent guard: canActivate: [AuthGuard, VendorRoleGuard]
 */

import React, { lazy } from 'react';
import { RouteConfig, RouteMetadata } from '../../types';

const RedirectionsConnect = lazy(() => import('@web/apps/Redirections/RedirectionsConnect'));

const AdmComponent = lazy(() =>
  import('@src/apps/NetworkManagement/Adm/admComponent').then((m) => ({ default: m.AdmComponent }))
);
const AffordabilitySummary = lazy(() =>
  import('@src/apps/AffordabilitySummary/AffordabilitySummary').then((m) => ({ default: m.AffordabilitySummary }))
);
const VBCConnected = lazy(() =>
  import('@src/apps/NetworkManagement/VBC/VBCConnected')
);
const CROConnected = lazy(() =>
  import('@src/apps/NetworkManagement/ContractRemediationOpportunities/CROConnected')
);
const SiteOfService = lazy(() =>
  import('@src/apps/SiteOfService/SiteOfService').then((m) => ({ default: m.SiteOfService }))
);
const DDPLabAndImaging = lazy(() =>
  import('@src/apps/DDPLabAndImaging/DDPLabAndImaging').then((m) => ({ default: m.DDPLabAndImaging }))
);
const RadiologyAndCardiology = lazy(() =>
  import('@src/apps/RadiologyAndCardiology/RadiologyAndCardiology').then((m) => ({ default: m.RadiologyAndCardiology }))
);
const AffordabilityDrugManagement = lazy(() =>
  import('@src/apps/AffordabilityDrugManagement/AffordabilityDrugManagement').then((m) => ({ default: m.AffordabilityDrugManagement }))
);
const AffordablitySummaryMedSourcing = lazy(() =>
  import('@src/apps/AffordablitySummaryMedSourcing/AffordablitySummaryMedSourcing').then((m) => ({ default: m.AffordablitySummaryMedSourcing }))
);
const HospitalReferenceLab = lazy(() =>
  import('@src/apps/HospitalReferenceLab/HospitalReferenceLab').then((m) => ({ default: m.HospitalReferenceLab }))
);
const Exemption = lazy(() =>
  import('@src/apps/Excemptions/Exemption').then((m) => ({ default: m.Exemption }))
);
const EXTERNAL_ACCESS_DENIED: Pick<RouteMetadata, 'externalAccess'> = {
  externalAccess: false,
};
const networkManagementRouteFrameStyles: React.CSSProperties = {
  width: '60rem',
  maxWidth: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
};

const NetworkManagementRouteFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={networkManagementRouteFrameStyles}>{children}</div>
);

const AdmRoute = () => (
  <NetworkManagementRouteFrame>
    <AdmComponent />
  </NetworkManagementRouteFrame>
);

const AffordabilitySummaryRoute = () => (
  <NetworkManagementRouteFrame>
    <AffordabilitySummary />
  </NetworkManagementRouteFrame>
);

const SiteOfServiceRoute = () => (
  <NetworkManagementRouteFrame>
    <SiteOfService />
  </NetworkManagementRouteFrame>
);

const HospitalReferenceLabRoute = () => (
  <NetworkManagementRouteFrame>
    <HospitalReferenceLab />
  </NetworkManagementRouteFrame>
);

const RadiologyAndCardiologyRoute = () => (
  <NetworkManagementRouteFrame>
    <RadiologyAndCardiology />
  </NetworkManagementRouteFrame>
);

const AffordablitySummaryMedSourcingRoute = () => (
  <NetworkManagementRouteFrame>
    <AffordablitySummaryMedSourcing />
  </NetworkManagementRouteFrame>
);

const DDPLabAndImagingRoute = () => (
  <NetworkManagementRouteFrame>
    <DDPLabAndImaging />
  </NetworkManagementRouteFrame>
);

const AffordabilityDrugManagementRoute = () => (
  <NetworkManagementRouteFrame>
    <AffordabilityDrugManagement />
  </NetworkManagementRouteFrame>
);

const VBCRoute = () => (
  <NetworkManagementRouteFrame>
    <VBCConnected />
  </NetworkManagementRouteFrame>
);

const ContractRemediationOpportunitiesRoute = () => (
  <NetworkManagementRouteFrame>
    <CROConnected />
  </NetworkManagementRouteFrame>
);

const ExemptionRoute = () => (
  <NetworkManagementRouteFrame>
    <Exemption />
  </NetworkManagementRouteFrame>
);
const RedirectionsRoute = () => (
  <NetworkManagementRouteFrame>
    <RedirectionsConnect />
  </NetworkManagementRouteFrame>
);

export const networkManagementRoutes: RouteConfig[] = [
  {
    path: 'network-management/adm-report',
    component: AdmRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'ADM Report', breadcrumb: 'Appropriate Decision Monitoring (ADM)' },
  },
  {
    path: 'network-management/affordability-summary',
    component: AffordabilitySummaryRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Affordability Summary', breadcrumb: 'UHN Network and Clinical Affordability Summary' },
  },
  {
    path: 'network-management/affordability-sos-details',
    component: SiteOfServiceRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Affordability SOS Details', breadcrumb: 'Affordability SOS Details' },
  },
  {
    path: 'network-management/affordability-sos-details/office',
    component: SiteOfServiceRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Affordability SOS Office', breadcrumb: 'Affordability SOS Details' },
  },
  {
    path: 'network-management/affordability-sos-details/colonoscopy',
    component: SiteOfServiceRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Affordability SOS Colonoscopy', breadcrumb: 'Affordability SOS Details' },
  },
  {
    path: 'network-management/affordability-sos-details/surgery',
    component: SiteOfServiceRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Affordability SOS Surgery', breadcrumb: 'Affordability SOS Details' },
  },
  {
    path: 'network-management/affordability-details',
    component: HospitalReferenceLabRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Affordability Details', breadcrumb: 'Affordability Details' },
  },
  {
    path: 'network-management/affordability-rad-card-pa',
    component: RadiologyAndCardiologyRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Radiology & Cardiology', breadcrumb: 'Radiology & Cardiology' },
  },
  {
    path: 'network-management/affordability-med-sourcing',
    component: AffordablitySummaryMedSourcingRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Medication Sourcing', breadcrumb: 'Medication Sourcing' },
  },
  {
    path: 'network-management/ddp-lab',
    component: DDPLabAndImagingRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'DDP Lab', breadcrumb: 'DDP Lab' },
  },
  {
    path: 'network-management/ddp-image',
    component: DDPLabAndImagingRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'DDP Image', breadcrumb: 'DDP Image' },
  },
  {
    path: 'network-management/affordability-edc-analyzer',
    component: AffordabilityDrugManagementRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'EDC Analyzer', breadcrumb: 'EDC Analyzer' },
  },
  {
    path: 'network-management/vbc',
    component: VBCRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'VBC', breadcrumb: 'Value Based Contracting' },
  },
  {
    path: 'network-management/contract-renewal-remediation',
    component: ContractRemediationOpportunitiesRoute,
    guards: ['auth', 'vendor'],
    data: { title: 'Contract Renewal & Remediation', breadcrumb: 'Contract Renewal & Remediation' },
  },
  {
    path: 'network-management/drug-management',
    component: AffordabilityDrugManagementRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Drug Management', breadcrumb: 'Drug Management' },
  },
  {
    path: 'network-management/drug-management/rare-complex',
    component: AffordabilityDrugManagementRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Drug Management - Rare Complex', breadcrumb: 'Drug Management' },
  },
  {
    path: 'network-management/exemption-summary',
    component: ExemptionRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Exemption Summary', breadcrumb: 'Exemption Summary' },
  },
  {
    path: 'network-management/redirections',
    component: RedirectionsRoute,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Redirections', breadcrumb: 'Redirections' },
  }
];
